/**
 * FRONT-2539: tables Eddy writes adapt to the width they land in.
 *
 * A Markdown table is model-written, so it has no column priorities to drop
 * by. Instead each table measures what it needs against the box it gets:
 *
 * - it fits: a normal table;
 * - it doesn't, and the box is at least `TABLE_CARDS_BELOW_PX` wide: the
 *   table scrolls sideways inside its own box with the first column pinned;
 * - it doesn't, and the box is narrower (phone, side panel): one card per
 *   row, the first cell as its title and the rest as "header: value" lines.
 *   A table of one or two columns never becomes cards: it already reads as
 *   label and value, so it wraps its cells instead.
 *
 * This module is the pure half: the layout decision, the width memory, the
 * column labels and the clipboard builders. Everything that touches the page
 * lives in `use-adaptive-tables.ts`, which works on the rendered DOM AFTER
 * `MarkdownRenderer` has put the sanitized reply on screen. Nothing here
 * edits the reply's HTML text: an earlier version spliced labels into the
 * sanitized string and a review reproduced a header being smuggled into a
 * link's attribute as a live `javascript:` URL. On the DOM a label is only
 * ever an attribute value set through the API, so it cannot become markup.
 */

import { escapeHtml } from './file-drag';

export type TableLayout = 'table' | 'scroll' | 'cards';

/** Below this box width a table that does not fit becomes cards, above it it scrolls. */
export const TABLE_CARDS_BELOW_PX = 480;

/** Below this box width a card puts each label above its value instead of beside it. */
export const CARD_LABEL_ABOVE_BELOW_PX = 320;

/** A column label longer than this is cut, so a huge header can't be copied onto every cell in full. */
export const MAX_LABEL_CHARS = 80;

export function decideTableLayout({
  needed,
  minimum,
  box,
  columns,
  current,
}: {
  /** The width the table wants with long cells wrapped at a readable measure. */
  needed: number;
  /**
   * The narrowest the table can go with every cell wrapped to its longest
   * word. Only consulted for a table of one or two columns.
   */
  minimum: number;
  /** The width its box actually has. */
  box: number;
  columns: number;
  current?: TableLayout;
}): TableLayout {
  // A hidden or collapsed box reports 0: nothing to decide until it shows.
  if (box <= 0) return current ?? 'scroll';
  if (needed <= box) return 'table';
  // One or two columns already read as "label: value" lines, so cards would
  // only add boxes around them. Such a table wraps its cells instead, and
  // scrolls only if even its longest words don't fit.
  if (columns <= 2) return minimum <= box ? 'table' : 'scroll';
  if (box >= TABLE_CARDS_BELOW_PX) return 'scroll';
  return 'cards';
}

/** What a table's content looks like, cheaply: enough to tell "unchanged" and "grew" apart. */
export interface TableSignature {
  /** The header row's text: a different header means a different table. */
  header: string;
  rows: number;
  textLength: number;
}

/** What the measuring hook remembers about one table between layouts. */
export interface TableMemory extends TableSignature {
  needed: number;
  minimum: number;
  layout: TableLayout;
}

/** Same table, same content: its widths can't have changed, so don't force a layout to re-measure them. */
export function sameTableContent(prior: TableMemory | undefined, signature: TableSignature): boolean {
  return (
    !!prior &&
    prior.header === signature.header &&
    prior.rows === signature.rows &&
    prior.textLength === signature.textLength
  );
}

/**
 * The widths to judge a table by. While Eddy is still writing, the rendered
 * html is replaced on every token and a table only GAINS content, so its
 * widths are kept grow-only: that is what makes a streaming table switch
 * layout at most once and never flick back. A table whose content shrank
 * or whose header changed is a different table and starts over, so a
 * renderer reused for other content never inherits a stale, too-wide
 * measurement.
 */
export function rememberTableWidths(
  prior: TableMemory | undefined,
  signature: TableSignature,
  measured: { needed: number; minimum: number },
): { needed: number; minimum: number; prior: TableMemory | undefined } {
  const same = prior && prior.header === signature.header ? prior : undefined;
  const grew = same && signature.textLength >= same.textLength ? same : undefined;
  return {
    needed: Math.max(grew?.needed ?? 0, measured.needed),
    minimum: Math.max(grew?.minimum ?? 0, measured.minimum),
    prior: same,
  };
}

/** One header's text as a short, single-line column label. */
export function cellLabel(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  const chars = Array.from(flat); // code points, so an emoji is never split in half
  return chars.length > MAX_LABEL_CHARS ? `${chars.slice(0, MAX_LABEL_CHARS - 1).join('')}…` : flat;
}

/** No table is labelled past this many columns, however many header cells or spans it declares. */
export const MAX_LABELLED_COLUMNS = 1000;

/**
 * One label per column, expanding a header cell's `colspan` so a value under
 * a spanning header still gets its name. Capped at `MAX_LABELLED_COLUMNS` in
 * total, so a hostile header row can't build a huge array.
 */
export function columnLabels(headers: { text: string; span: number }[]): string[] {
  const labels: string[] = [];
  for (const { text, span } of headers) {
    const label = cellLabel(text);
    const count = Number.isFinite(span) ? Math.max(1, Math.floor(span)) : 1;
    for (let i = 0; i < count; i++) {
      if (labels.length >= MAX_LABELLED_COLUMNS) return labels;
      labels.push(label);
    }
  }
  return labels;
}

/** One cell's text, flattened so a paste keeps its row and column. */
function flatCell(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Tab-separated text: what Excel and Sheets split into real columns on paste. */
export function tableToTsv(rows: string[][]): string {
  return rows.map((row) => row.map(flatCell).join('\t')).join('\n');
}

/**
 * A minimal, style-free HTML table — the flavour Word, Outlook and Notion
 * paste as a real table. Text only, escaped: none of the rendered markup
 * (links, highlights, our own labels) travels with it. The first row is the
 * header.
 */
export function tableToHtml(rows: string[][]): string {
  if (rows.length === 0) return '';
  const [head, ...body] = rows;
  const cells = (row: string[], tag: 'th' | 'td') =>
    row.map((c) => `<${tag}>${escapeHtml(flatCell(c))}</${tag}>`).join('');
  return (
    `<table><thead><tr>${cells(head, 'th')}</tr></thead>` +
    `<tbody>${body.map((r) => `<tr>${cells(r, 'td')}</tr>`).join('')}</tbody></table>`
  );
}
